import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useQuery } from 'react-query';
import axios from 'axios';
import SubscriptionCard from '../../components/Subscriptions/SubscriptionCard';
import PaymentModal from '../../components/Subscriptions/PaymentModal';
import { CheckCircleIcon, CheckIcon, XMarkIcon } from '@heroicons/react/24/outline';

// Cellules du comparatif : « ✅ » / « ❌ » affichés en icônes SVG.
const renderCell = (value) => {
  if (typeof value !== 'string') return value;
  if (value.startsWith('✅')) {
    const suffixe = value.slice(1).trim();
    return (
      <span className="inline-flex items-center gap-1">
        <CheckIcon className="w-5 h-5 text-green-600" aria-label="Inclus" />
        {suffixe && <span>{suffixe}</span>}
      </span>
    );
  }
  if (value === '❌') return <XMarkIcon className="w-5 h-5 text-red-500 mx-auto" aria-label="Non inclus" />;
  return value;
};

const Subscriptions = () => {
  const { user } = useAuth();
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);

  const { data: plans = [], isLoading } = useQuery(
    'subscription-plans',
    () => axios.get('/api/subscriptions/plans').then(r => r.data)
  );

  const { data: currentSubscription } = useQuery(
    'current-subscription',
    () => axios.get('/api/subscriptions/current').then(r => r.data),
    { enabled: !!user }
  );

  const handleSelectPlan = (plan) => {
    // Plan Basique : le module (Gestion ou AgroGestion) se choisit sur la plateforme, au paiement
    if (plan.souscriptionPlateforme) {
      window.location.href = 'https://app.innovactive360.cloud/tarifs';
      return;
    }
    setSelectedPlan(plan);
    setShowPaymentModal(true);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-text mb-4">Choisissez votre plan</h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Débloquez toutes les fonctionnalités de la plateforme Innovactive360° et connectez-vous
            avec les meilleurs entrepreneurs et investisseurs.
          </p>
        </div>

        {/* Abonnement actuel */}
        {currentSubscription && (
          <div className="mb-8 p-6 bg-green-50 border border-green-200 rounded-lg">
            <div className="flex items-center">
              <CheckCircleIcon className="h-8 w-8 text-green-500 mr-3" />
              <div>
                <h3 className="text-lg font-semibold text-green-800">
                  Plan actuel : {currentSubscription.planName}
                </h3>
                {currentSubscription.renewalDate && (
                  <p className="text-green-600 text-sm">
                    Renouvellement le {new Date(currentSubscription.renewalDate).toLocaleDateString('fr-FR')}
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Plans */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
          {plans.map(plan => (
            <SubscriptionCard
              key={plan.id}
              plan={plan}
              isPopular={plan.id === 'pro'}
              isCurrentPlan={currentSubscription?.planId === plan.id}
              onSelectPlan={handleSelectPlan}
            />
          ))}
        </div>

        {/* Tableau comparatif */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 mb-12">
          <h2 className="text-2xl font-bold text-text mb-6 text-center">Comparaison détaillée</h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-4 px-4 font-semibold text-text">Fonctionnalités</th>
                  {plans.map(plan => (
                    <th key={plan.id} className="text-center py-4 px-4 font-semibold text-text">{plan.name}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {[
                  { label: 'Projets max', key: (p) => p.limits?.maxProjects == null ? 'Illimité' : p.limits.maxProjects === 0 ? '—' : p.limits.maxProjects },
                  { label: 'Contacts investisseurs', key: (p) => p.limits?.maxContacts > 0 ? `${p.limits.maxContacts}/mois` : '—' },
                  { label: "Bourse d'Idées", key: (p) => p.limits?.bourseIdees ? '✅' : '❌' },
                  { label: 'Module MOOC', key: (p) => p.limits?.mooc ? '✅' : '❌' },
                  { label: 'Business Plan', key: (p) => p.id !== 'basique' ? '✅' : '❌' },
                  { label: 'Collaborateurs', key: (p) => p.id === 'promax' ? '✅ (10 max)' : '❌' },
                  { label: 'Add-on IA', key: (p) => p.id === 'boost-ia' ? '✅' : '—' },
                  { label: 'Support', key: (p) => {
                    if (p.id === 'basique') return '—';
                    if (p.id === 'pro') return 'Email 24h';
                    if (p.id === 'promax') return 'Prioritaire';
                    if (p.id === 'boost-ia') return 'Prioritaire';
                    return '—';
                  }}
                ].map(({ label, key }) => (
                  <tr key={label} className="border-b border-gray-100">
                    <td className="py-3 px-4 text-gray-700">{label}</td>
                    {plans.map(plan => (
                      <td key={plan.id} className="text-center py-3 px-4">{renderCell(key(plan))}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* FAQ */}
        <div>
          <h2 className="text-2xl font-bold text-text mb-6 text-center">Questions fréquentes</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[
              {
                q: 'Puis-je changer de plan à tout moment ?',
                a: 'Oui, vous pouvez changer de plan à tout moment. Avec le plan Basique, le changement de module (Gestion ou AgroGestion) prend effet à la fin de la période payée.'
              },
              {
                q: "Y a-t-il un engagement ?",
                a: "Non, tous nos plans sont sans engagement. Vous pouvez annuler votre abonnement à tout moment."
              },
              {
                q: 'Les paiements sont-ils sécurisés ?',
                a: "Oui, nous utilisons FedaPay pour traiter tous les paiements de manière sécurisée. Vos données bancaires ne transitent jamais par nos serveurs."
              },
              {
                q: 'Dans quelle devise suis-je facturé ?',
                a: "Les prix sont affichés et payés en francs CFA (FCFA), via FedaPay. Le prix du plan Basique est un prix promotionnel."
              }
            ].map(({ q, a }) => (
              <div key={q} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="font-semibold text-text mb-2">{q}</h3>
                <p className="text-gray-600 text-sm">{a}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Modal paiement Fedapay */}
        <PaymentModal
          plan={selectedPlan}
          isOpen={showPaymentModal}
          onClose={() => {
            setShowPaymentModal(false);
            setSelectedPlan(null);
          }}
        />
      </div>
    </div>
  );
};

export default Subscriptions;
